/**
 * TopKunden — en iyi müşteriler listesi (ciroya göre).
 *
 * Storniert siparişler hariç, lead bazında ciro toplanır ve sıralanır.
 * Başlangıçta `initialCount` satır gösterilir; "Alle anzeigen" ile
 * `expandCount` satıra genişler.
 *
 * @param {Array}  orders       - sipariş listesi (sayfa tarafından çekilir)
 * @param {number} initialCount - başlangıç satır sayısı (örn. 5)
 * @param {number} expandCount  - genişletilmiş satır sayısı (örn. 10 / 50)
 * @param {string} title        - kart başlığı
 */
import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trophy, ChevronDown, ChevronUp } from 'lucide-react';

const MEDALS = ['🥇', '🥈', '🥉'];

export default function TopKunden({
  orders = [],
  initialCount = 5,
  expandCount = 10,
  title = 'Top Kunden',
}) {
  const [expanded, setExpanded] = useState(false);

  const ranking = useMemo(() => {
    const byCustomer = new Map();
    for (const o of orders) {
      // Sadece gerçekleşen siparişler ciroya girer — teklifler (Angebot)
      // ve iptaller hariç.
      if (o.status === 'Storniert') continue;
      if (o.order_type === 'Angebot') continue;
      const key = o.lead_id || `name:${o.lead_name || 'Unbekannt'}`;
      const entry = byCustomer.get(key) || {
        leadId: o.lead_id || null,
        name: o.lead_name || 'Unbekannt',
        orders: 0,
        revenue: 0,
      };
      entry.orders += 1;
      entry.revenue += parseFloat(o.total ?? o.gesamtsumme ?? 0) || 0;
      byCustomer.set(key, entry);
    }
    return [...byCustomer.values()].sort((a, b) => b.revenue - a.revenue);
  }, [orders]);

  const visible = ranking.slice(0, expanded ? expandCount : initialCount);
  const totalRevenue = ranking.reduce((s, r) => s + r.revenue, 0);

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-slate-500 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" />
            {title}
          </span>
          <span className="text-xs font-normal text-slate-400">
            Gesamt: € {totalRevenue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {visible.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">Noch keine Umsätze</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {visible.map((r, idx) => (
              <div key={r.leadId || r.name} className="flex items-center gap-3 py-2.5">
                <span className="w-7 text-center text-sm font-bold text-slate-400 shrink-0">
                  {MEDALS[idx] || `${idx + 1}.`}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{r.name}</p>
                  <p className="text-[11px] text-slate-400">{r.orders} Bestellung(en)</p>
                </div>
                <span className="text-sm font-black text-slate-900 shrink-0">
                  € {r.revenue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                </span>
              </div>
            ))}
          </div>
        )}

        {ranking.length > initialCount && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-2 text-slate-500"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <><ChevronUp className="w-4 h-4 mr-1" /> Weniger anzeigen</>
            ) : (
              <><ChevronDown className="w-4 h-4 mr-1" /> Alle anzeigen (Top {Math.min(expandCount, ranking.length)})</>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
